import { useState } from 'react';
import {
  TrendingUp, DollarSign, ShoppingBag,
  Download, ArrowUpRight, ArrowDownRight, Clock, Star,
  BarChart3, PieChart, Activity,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { mockReports, mockStats } from '@/services/mockData';

export default function ReportsPage() {
  const { t, formatCurrency, language } = useTranslation();
  const [dateRange, setDateRange] = useState('week');

  const reports = mockReports;
  const stats = mockStats;

  // Bar chart helper — renders a simple CSS bar chart
  const maxRevenue = Math.max(...reports.revenue.daily.map(d => d.amount));
  const maxOrders = Math.max(...reports.orders.daily.map(d => d.count));
  const maxPeakHour = Math.max(...reports.peak_hours.map(h => h.orders));

  const dayNames = language === 'ar'
    ? ['سبت', 'أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة']
    : ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  const dateRangeOptions = [
    { key: 'today', label: t('today') },
    { key: 'week', label: language === 'ar' ? 'هذا الأسبوع' : 'This Week' },
    { key: 'month', label: t('thisMonth') },
    { key: 'quarter', label: language === 'ar' ? 'ربع سنوي' : 'Quarter' },
  ];

  // Summary Stats
  const summaryStats = [
    {
      label: language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue',
      value: formatCurrency(reports.revenue.total),
      change: '+18.5%',
      isPositive: true,
      icon: DollarSign,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    },
    {
      label: language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders',
      value: reports.orders.total.toLocaleString(),
      change: '+12.3%',
      isPositive: true,
      icon: ShoppingBag,
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      label: language === 'ar' ? 'متوسط وقت التوصيل' : 'Avg Delivery Time',
      value: `${stats.avg_delivery_time} ${language === 'ar' ? 'دقيقة' : 'min'}`,
      change: '-5.2%',
      isPositive: true,
      icon: Clock,
      color: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      label: language === 'ar' ? 'رضا العملاء' : 'Customer Satisfaction',
      value: `${stats.customer_satisfaction}/5`,
      change: '+2.1%',
      isPositive: true,
      icon: Star,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-100 dark:bg-amber-900/30',
    },
  ];

  const statusColors: Record<string, string> = {
    delivered: 'bg-emerald-500',
    cancelled: 'bg-red-500',
    pending: 'bg-amber-500',
    preparing: 'bg-indigo-500',
    on_the_way: 'bg-purple-500',
  };

  const statusLabels: Record<string, string> = {
    delivered: language === 'ar' ? 'تم التوصيل' : 'Delivered',
    cancelled: language === 'ar' ? 'ملغي' : 'Cancelled',
    pending: language === 'ar' ? 'قيد الانتظار' : 'Pending',
    preparing: language === 'ar' ? 'جاري التحضير' : 'Preparing',
    on_the_way: language === 'ar' ? 'في الطريق' : 'On the Way',
  };

  const totalStatusOrders = Object.values(reports.status_breakdown).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('reports')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? 'تحليلات وإحصائيات مفصلة' : 'Detailed analytics & statistics'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-1">
            {dateRangeOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setDateRange(opt.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === opt.key
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            <Download size={18} />
            <span className="hidden sm:inline">{t('export')}</span>
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryStats.map((stat, i) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-11 h-11 ${stat.bg} rounded-xl flex items-center justify-center`}>
                <stat.icon size={20} className={stat.color} />
              </div>
              <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                stat.isPositive
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              }`}>
                {stat.isPositive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {stat.change}
              </span>
            </div>
            <p className="text-2xl font-bold text-slate-800 dark:text-white">{stat.value}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <BarChart3 size={18} className="text-emerald-500" />
                {language === 'ar' ? 'الإيرادات اليومية' : 'Daily Revenue'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {language === 'ar' ? 'المتوسط:' : 'Average:'} {formatCurrency(reports.revenue.avg)}
              </p>
            </div>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(reports.revenue.total)}</p>
          </div>
          <div className="flex items-end gap-2 h-48">
            {reports.revenue.daily.map((day, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full relative group">
                  <div
                    className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-lg transition-all hover:from-emerald-600 hover:to-emerald-500 cursor-pointer"
                    style={{ height: `${(day.amount / maxRevenue) * 160}px` }}
                  />
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {formatCurrency(day.amount)}
                  </div>
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">{dayNames[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Orders Chart */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <Activity size={18} className="text-blue-500" />
                {language === 'ar' ? 'الطلبات اليومية' : 'Daily Orders'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {language === 'ar' ? 'المتوسط:' : 'Average:'} {reports.orders.avg} {language === 'ar' ? 'طلب' : 'orders'}
              </p>
            </div>
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{reports.orders.total.toLocaleString()}</p>
          </div>
          <div className="flex items-end gap-2 h-48">
            {reports.orders.daily.map((day, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full relative group">
                  <div
                    className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all hover:from-blue-600 hover:to-blue-500 cursor-pointer"
                    style={{ height: `${(day.count / maxOrders) * 160}px` }}
                  />
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {day.count} {language === 'ar' ? 'طلب' : 'orders'}
                  </div>
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">{dayNames[i]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Status Breakdown */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-6">
            <PieChart size={18} className="text-purple-500" />
            {language === 'ar' ? 'توزيع حالات الطلبات' : 'Order Status Distribution'}
          </h3>

          {/* Visual Pie */}
          <div className="flex justify-center mb-6">
            <div className="relative w-40 h-40">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {(() => {
                  let offset = 0;
                  const entries = Object.entries(reports.status_breakdown);
                  return entries.map(([status, count]) => {
                    const pct = (count / totalStatusOrders) * 100;
                    const circumference = Math.PI * 36 * 2;
                    const dash = (pct / 100) * circumference;
                    const el = (
                      <circle
                        key={status}
                        cx="50"
                        cy="50"
                        r="36"
                        fill="none"
                        stroke={status === 'delivered' ? '#10b981' : status === 'cancelled' ? '#ef4444' : status === 'pending' ? '#f59e0b' : status === 'preparing' ? '#6366f1' : '#a855f7'}
                        strokeWidth="20"
                        strokeDasharray={`${dash} ${circumference - dash}`}
                        strokeDashoffset={-(offset / 100) * circumference}
                      />
                    );
                    offset += pct;
                    return el;
                  });
                })()}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-2xl font-bold text-slate-800 dark:text-white">{totalStatusOrders}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'طلب' : 'orders'}</p>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="space-y-2.5">
            {Object.entries(reports.status_breakdown).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${statusColors[status]}`} />
                  <span className="text-sm text-slate-600 dark:text-slate-300">{statusLabels[status]}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800 dark:text-white">{count}</span>
                  <span className="text-xs text-slate-400">({((count / totalStatusOrders) * 100).toFixed(1)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Cafes */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-6">
            <TrendingUp size={18} className="text-emerald-500" />
            {language === 'ar' ? 'أفضل المقاهي' : 'Top Cafes'}
          </h3>
          <div className="space-y-4">
            {reports.top_cafes.map((cafe, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' :
                  i === 1 ? 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300' :
                  i === 2 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600' :
                  'bg-slate-100 dark:bg-slate-700 text-slate-500'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{cafe.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{cafe.orders} {language === 'ar' ? 'طلب' : 'orders'}</p>
                </div>
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                  {formatCurrency(cafe.revenue)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-6">
            <Star size={18} className="text-amber-500" />
            {language === 'ar' ? 'أفضل المنتجات' : 'Top Products'}
          </h3>
          <div className="space-y-4">
            {reports.top_products.map((product, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-white">{product.name}</span>
                    <span className="text-xs text-slate-400">({product.orders} {language === 'ar' ? 'طلب' : 'orders'})</span>
                  </div>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(product.revenue)}</span>
                </div>
                <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-full rounded-full transition-all ${
                      i === 0 ? 'bg-emerald-500' :
                      i === 1 ? 'bg-blue-500' :
                      i === 2 ? 'bg-purple-500' :
                      i === 3 ? 'bg-amber-500' :
                      'bg-rose-500'
                    }`}
                    style={{ width: `${(product.orders / reports.top_products[0].orders) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Peak Hours */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-6">
          <Clock size={18} className="text-rose-500" />
          {language === 'ar' ? 'ساعات الذروة' : 'Peak Hours'}
        </h3>
        <div className="flex items-end gap-1.5 h-44">
          {reports.peak_hours.map((hour, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full relative group">
                <div
                  className={`w-full rounded-t-md transition-all cursor-pointer ${
                    hour.orders === maxPeakHour
                      ? 'bg-gradient-to-t from-rose-500 to-rose-400'
                      : hour.orders > maxPeakHour * 0.7
                      ? 'bg-gradient-to-t from-rose-400 to-rose-300'
                      : hour.orders > maxPeakHour * 0.4
                      ? 'bg-gradient-to-t from-rose-300 to-rose-200'
                      : 'bg-gradient-to-t from-rose-200 to-rose-100 dark:from-rose-900/40 dark:to-rose-900/20'
                  }`}
                  style={{ height: `${(hour.orders / maxPeakHour) * 140}px` }}
                />
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {hour.orders} {language === 'ar' ? 'طلب' : 'orders'}
                </div>
              </div>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 whitespace-nowrap">{hour.hour.split(':')[0]}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
          <span>{language === 'ar' ? 'صباحاً' : 'AM'}</span>
          <span>{language === 'ar' ? 'مساءً' : 'PM'}</span>
        </div>
      </div>
    </div>
  );
}
